import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert,
    IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControl, InputLabel, Select, MenuItem, Tooltip, InputAdornment,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    PersonOff as DeactivateIcon,
    PersonAdd as ReactivateIcon,
    CreditCard as CardIcon,
    AccountCircle as AccountIcon,
    Delete as RemoveCardIcon,
} from '@mui/icons-material';
import { accountsAPI, carTypesAPI } from '../services/apiService';
import SquarePaymentForm from './SquarePaymentForm';
import AddressAutocomplete from './AddressAutocomplete';
import { useAlert } from '../contexts/AlertContext';

const EMPTY_FORM = {
    accountId: 0,
    name: '',
    company: '',
    phoneNumber: '',
    email: '',
    defaultPickup: '',
    defaultDropoff: '',
    defaultPassengers: '',
    defaultCarType: '',
    discountType: '',
    discountValue: '',
    isActive: true,
};

const AccountsManager = () => {
    const [accounts, setAccounts] = useState([]);
    const [carTypes, setCarTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Add/Edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = create, account obj = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    // Card management (inline in add/edit dialog)
    const [dialogCardNonce, setDialogCardNonce] = useState(null);
    const [dialogRemoveCard, setDialogRemoveCard] = useState(false);
    const dialogSquareRef = useRef(null);

    // Separate quick-card dialog (kept as convenience shortcut from table)
    const [cardDialogOpen, setCardDialogOpen] = useState(false);
    const [cardAccount, setCardAccount] = useState(null);
    const [cardNonce, setCardNonce] = useState(null);
    const [cardSaving, setCardSaving] = useState(false);
    const squareRef = useRef(null);

    const { showAlert } = useAlert();

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const data = await accountsAPI.getAll();
            setAccounts(Array.isArray(data) ? data : []);
            setError(null);
        } catch (err) {
            setError('Failed to load accounts: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        carTypesAPI.getAll()
            .then(data => setCarTypes(Array.isArray(data) ? data : []))
            .catch(() => setCarTypes([]));
    }, [load]);

    // ── Add / Edit ──────────────────────────────────────────────────────────────

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormError(null);
        setDialogCardNonce(null);
        setDialogRemoveCard(false);
        setDialogOpen(true);
    };

    const openEdit = (account) => {
        setEditing(account);
        setForm({
            accountId: account.accountId,
            name: account.name ?? '',
            company: account.company ?? '',
            phoneNumber: account.phoneNumber ?? '',
            email: account.email ?? '',
            defaultPickup: account.defaultPickup ?? '',
            defaultDropoff: account.defaultDropoff ?? '',
            defaultPassengers: account.defaultPassengers ?? '',
            defaultCarType: account.defaultCarType ?? '',
            discountType: account.discountType ?? '',
            discountValue: account.discountValue ?? '',
            isActive: account.isActive,
        });
        setFormError(null);
        setDialogCardNonce(null);
        setDialogRemoveCard(false);
        setDialogOpen(true);
    };

    const handleFormChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!form.name.trim()) { setFormError('Name is required.'); return; }
        setSaving(true);
        setFormError(null);
        try {
            const payload = {
                ...form,
                defaultPassengers: form.defaultPassengers === '' ? null : parseInt(form.defaultPassengers, 10),
                defaultCarType: form.defaultCarType === '' ? null : parseInt(form.defaultCarType, 10),
                discountValue: form.discountValue === '' ? 0 : parseFloat(form.discountValue),
                discountType: form.discountType || null,
            };
            let accountId;
            if (editing) {
                await accountsAPI.update(editing.accountId, payload);
                accountId = editing.accountId;
            } else {
                const created = await accountsAPI.create(payload);
                accountId = created.accountId;
            }
            // Handle card changes
            if (dialogRemoveCard && editing?.cardId) {
                await accountsAPI.removeCard(accountId);
            } else if (dialogCardNonce) {
                await accountsAPI.attachCard(accountId, dialogCardNonce, form.name);
            }
            setSuccess(editing ? 'Account updated.' : 'Account created.');
            setDialogOpen(false);
            load();
        } catch (err) {
            const msg = err.response?.data?.message || err.message;
            setFormError(msg);
        } finally {
            setSaving(false);
        }
    };

    // ── Deactivate ───────────────────────────────────────────────────────────────

    const handleDeactivate = (account) => {
        showAlert(
            'Deactivate Account',
            `Deactivate "${account.name}"? Historical ride links will be preserved. You can search for it in the table but it won't appear in New Call search.`,
            [
                { label: 'Cancel', color: 'inherit' },
                {
                    label: 'Deactivate', color: 'error', onClick: async () => {
                        try {
                            await accountsAPI.deactivate(account.accountId);
                            setSuccess('Account deactivated.');
                            load();
                        } catch (err) {
                            setError('Failed to deactivate: ' + (err.response?.data?.message || err.message));
                        }
                    }
                }
            ]
        );
    };

    const handleReactivate = (account) => {
        showAlert(
            'Reactivate Account',
            `Reactivate "${account.name}"? It will appear again in New Call search.`,
            [
                { label: 'Cancel', color: 'inherit' },
                {
                    label: 'Reactivate', color: 'success', onClick: async () => {
                        try {
                            await accountsAPI.reactivate(account.accountId);
                            setSuccess('Account reactivated.');
                            load();
                        } catch (err) {
                            setError('Failed to reactivate: ' + (err.response?.data?.message || err.message));
                        }
                    }
                }
            ]
        );
    };

    // ── Card management ──────────────────────────────────────────────────────────

    const openCardDialog = (account) => {
        setCardAccount(account);
        setCardNonce(null);
        setCardDialogOpen(true);
    };

    const handleCardSave = async () => {
        if (!cardNonce) { return; }
        setCardSaving(true);
        try {
            await accountsAPI.attachCard(cardAccount.accountId, cardNonce, cardAccount.name);
            setSuccess(`Card saved to account "${cardAccount.name}".`);
            setCardDialogOpen(false);
            load();
        } catch (err) {
            setError('Failed to save card: ' + (err.response?.data?.message || err.message));
        } finally {
            setCardSaving(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────────

    const discountLabel = (account) => {
        if (!account.discountType || !account.discountValue) return '—';
        return account.discountType === 'percent'
            ? `${account.discountValue}%`
            : `$${parseFloat(account.discountValue).toFixed(2)}`;
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={600}>
                    <AccountIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Customer Accounts
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    New Account
                </Button>
            </Box>

            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Company</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Discount</TableCell>
                                <TableCell>Card</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {accounts.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No accounts yet. Create one to get started.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                            {accounts.map((account) => (
                                <TableRow key={account.accountId} sx={{ opacity: account.isActive ? 1 : 0.5 }}>
                                    <TableCell><Typography fontWeight={500}>{account.name}</Typography></TableCell>
                                    <TableCell>{account.company || '—'}</TableCell>
                                    <TableCell>{account.phoneNumber || '—'}</TableCell>
                                    <TableCell>{account.email || '—'}</TableCell>
                                    <TableCell>
                                        {account.discountValue > 0
                                            ? <Chip size="small" label={discountLabel(account)} color="success" variant="outlined" />
                                            : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {account.cardId
                                            ? <Chip size="small" icon={<CardIcon />} label={`${account.cardBrand ?? ''} ···${account.cardLast4 ?? ''}`} color="primary" variant="outlined" />
                                            : <Typography variant="caption" color="text.secondary">None</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={account.isActive ? 'Active' : 'Inactive'} color={account.isActive ? 'success' : 'default'} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Add / Update Card">
                                            <IconButton size="small" onClick={() => openCardDialog(account)} disabled={!account.isActive}>
                                                <CardIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => openEdit(account)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {account.isActive ? (
                                            <Tooltip title="Deactivate">
                                                <IconButton size="small" color="error" onClick={() => handleDeactivate(account)}>
                                                    <DeactivateIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title="Reactivate">
                                                <IconButton size="small" color="success" onClick={() => handleReactivate(account)}>
                                                    <ReactivateIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* ── Add / Edit Dialog ── */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? `Edit: ${editing.name}` : 'New Account'}</DialogTitle>
                <DialogContent dividers>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    <TextField label="Name *" fullWidth margin="dense" value={form.name} onChange={e => handleFormChange('name', e.target.value)} />
                    <TextField label="Company" fullWidth margin="dense" value={form.company} onChange={e => handleFormChange('company', e.target.value)} />
                    <TextField label="Phone" fullWidth margin="dense" value={form.phoneNumber} onChange={e => handleFormChange('phoneNumber', e.target.value)} />
                    <TextField label="Email" fullWidth margin="dense" type="email" value={form.email} onChange={e => handleFormChange('email', e.target.value)} />

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Defaults (optional)</Typography>
                    <AddressAutocomplete
                        label="Default Pickup"
                        value={form.defaultPickup}
                        onChange={(address) => handleFormChange('defaultPickup', address)}
                        onInputChange={(val) => handleFormChange('defaultPickup', val)}
                        margin="dense"
                    />
                    <AddressAutocomplete
                        label="Default Dropoff"
                        value={form.defaultDropoff}
                        onChange={(address) => handleFormChange('defaultDropoff', address)}
                        onInputChange={(val) => handleFormChange('defaultDropoff', val)}
                        margin="dense"
                    />
                    <TextField label="Default Passengers" fullWidth margin="dense" type="number" inputProps={{ min: 1 }} value={form.defaultPassengers} onChange={e => handleFormChange('defaultPassengers', e.target.value)} />
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Default Car Type</InputLabel>
                        <Select
                            label="Default Car Type"
                            value={form.defaultCarType}
                            onChange={e => handleFormChange('defaultCarType', e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            {carTypes.map(ct => (
                                <MenuItem key={ct.id} value={ct.id}>{ct.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Discount (optional)</Typography>
                    <Box display="flex" gap={1}>
                        <FormControl margin="dense" sx={{ minWidth: 140 }}>
                            <InputLabel>Discount Type</InputLabel>
                            <Select label="Discount Type" value={form.discountType} onChange={e => handleFormChange('discountType', e.target.value)}>
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="amount">Fixed Amount ($)</MenuItem>
                                <MenuItem value="percent">Percentage (%)</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            label="Value"
                            margin="dense"
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            value={form.discountValue}
                            onChange={e => handleFormChange('discountValue', e.target.value)}
                            disabled={!form.discountType}
                            InputProps={{
                                startAdornment: form.discountType === 'amount' ? <InputAdornment position="start">$</InputAdornment> : null,
                                endAdornment: form.discountType === 'percent' ? <InputAdornment position="end">%</InputAdornment> : null,
                            }}
                            sx={{ flexGrow: 1 }}
                        />
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Card on File (optional)</Typography>
                    {editing?.cardId && !dialogRemoveCard && !dialogCardNonce && (
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Chip
                                icon={<CardIcon />}
                                label={`${editing.cardBrand ?? ''} ···${editing.cardLast4 ?? ''}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                            <Tooltip title="Remove card">
                                <IconButton size="small" color="error" onClick={() => setDialogRemoveCard(true)}>
                                    <RemoveCardIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    {dialogRemoveCard && (
                        <Alert
                            severity="warning"
                            sx={{ mb: 1 }}
                            action={
                                <Button size="small" onClick={() => setDialogRemoveCard(false)}>Undo</Button>
                            }
                        >
                            Card will be removed when you save.
                        </Alert>
                    )}
                    {!dialogRemoveCard && (
                        <Box>
                            {editing?.cardId && (
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Enter a new card below to replace the existing one.
                                </Typography>
                            )}
                            <SquarePaymentForm
                                ref={dialogSquareRef}
                                onTokenGenerated={(token) => setDialogCardNonce(token)}
                                onTokenCleared={() => setDialogCardNonce(null)}
                                onError={(err) => setFormError(err)}
                            />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? <CircularProgress size={18} /> : (editing ? 'Save Changes' : 'Create Account')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Card Dialog ── */}
            <Dialog open={cardDialogOpen} onClose={() => setCardDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>
                    {cardAccount?.cardId ? 'Replace Card on File' : 'Add Card on File'}
                    {cardAccount && <Typography variant="body2" color="text.secondary">{cardAccount.name}</Typography>}
                </DialogTitle>
                <DialogContent dividers>
                    {cardAccount?.cardId && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Current card: {cardAccount.cardBrand} ···{cardAccount.cardLast4}. Entering a new card will replace it.
                        </Alert>
                    )}
                    <SquarePaymentForm
                        ref={squareRef}
                        onTokenGenerated={(token) => setCardNonce(token)}
                        onTokenCleared={() => setCardNonce(null)}
                        onError={(err) => setError(err)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCardDialogOpen(false)} disabled={cardSaving}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCardSave}
                        disabled={!cardNonce || cardSaving}
                        startIcon={cardSaving ? <CircularProgress size={16} /> : <CardIcon />}
                    >
                        Save Card
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AccountsManager;
